import React, {useCallback} from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getPlayerById} from "modules/selectors";
import {BaseButton, PlayerCard, PlayerInfo, PlayerName, PlayerTop} from "components/Common";
import {PlayerEmpty} from "components/PlayerEmpty";
import {getPlayerName, toLowerCase} from "modules/utils/helpers";
import {removePlayerFromTeam, openPlayerDetailsModal} from "modules/actions";
import {PlayerPositionEnum} from "modules/enums";
import {IS_PLAYER_MODAL_DISABLED} from "modules/constants";
import {Jersey, SquadLogo} from "components";
import {breakpoints} from "assets/theme";
import {ReactComponent as RemoveIcon} from "assets/images/icons/remove.svg";

const Relative = styled.div`
	position: relative;
`;

const RemoveButton = styled(BaseButton)`
	cursor: pointer;
	position: absolute;
	right: 0;
	top: -15px;
	z-index: 5;
	width: 30px;
	height: 30px;

	@media screen and (max-width: ${breakpoints.mobile}) {
		right: -10px;
	}
`;

interface IProps {
	playerId: number;
	position: PlayerPositionEnum;
}

export const PlayerFilled: React.FC<IProps> = ({playerId, position}) => {
	const dispatch = useDispatch();
	const player = useSelector(getPlayerById)(playerId);
	const {t} = useTranslation();

	const handleRemove = useCallback(() => {
		dispatch(removePlayerFromTeam(position));
	}, [dispatch, position]);

	const openPlayerModalHandler = useCallback(() => {
		if (!player || IS_PLAYER_MODAL_DISABLED) {
			return;
		}

		dispatch(openPlayerDetailsModal({id: player.id}));
	}, [player, dispatch]);

	if (!player) {
		return <PlayerEmpty position={position} />;
	}

	return (
		<Relative>
			<RemoveButton onClick={handleRemove}>
				<RemoveIcon />
			</RemoveButton>
			<PlayerCard onClick={openPlayerModalHandler}>
				<PlayerTop>
					<Jersey image={player.image} alt={player.firstName} />

					<SquadLogo squadId={player.squadId} />
				</PlayerTop>

				<PlayerInfo>
					<PlayerName>{getPlayerName(player)}</PlayerName>
					<div>{t(toLowerCase(`game.position.${position}`))}</div>
				</PlayerInfo>
			</PlayerCard>
		</Relative>
	);
};
